import { Directive, Input, OnInit, OnChanges, SimpleChanges, DestroyRef, inject } from '@angular/core';
import { FormData } from '@lx/core/forms/form-data.model';
import { NotificationService } from '@lx/shared/logger';
import { BehaviorSubject, filter, Subject, switchMap, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive()
export abstract class FormDataValidityBaseDirective<T> implements OnInit, OnChanges {
  @Input() formData: FormData<T> | undefined;
  @Input() validationRequests = 0;

  readonly enabledValidation$ = new BehaviorSubject(false);
  protected readonly validation$ = new Subject<void>();

  protected readonly destroyRef = inject(DestroyRef);
  protected readonly notificationService = inject(NotificationService);

  ngOnInit(): void {
    if (!this.formData) {
      this.notificationService.warn(this.getNoFormDataWarningMessage());
      return;
    }

    this.enabledValidation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((enabled) => {
      if (enabled) {
        this.validation$.next();
      }
    });

    this.validation$
      .pipe(
        switchMap(() => this.enabledValidation$.pipe(take(1))),
        filter((enabled) => enabled),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.formData.isValid = this.calculateFormDataValidity();
      });
  }

  ngOnChanges({ validationRequests }: SimpleChanges) {
    if (validationRequests?.currentValue > 0) {
      this.enabledValidation$.next(true);
    }
  }

  protected calculateFormDataValidity(): boolean {
    return this.formData.value !== undefined && this.formData.value !== null && this.formData.value !== '';
  }

  protected abstract getNoFormDataWarningMessage(): string;
}
